import { Accordion } from "@mui/material";
import { createTheme, ThemeProvider} from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/Help";
import {
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  FormControl,
  Grid,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Toolbar,
  Typography
} from "@mui/material";
import axios from "axios";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import ColorConstants from "../../../../defaults/ColorConstants";
import FieldNames from "../../../../defaults/FieldNames.json";
import { useIFPRSGlobalContext } from "../../../../IFPRSGlobalContext";
import { formatDateForDownloadFiles, getDaysBeforeDate } from "../../../../utils/DateHelper";
import {
  generateStaticDownloadUrl,
  requestSingleDownload
} from "../../../../utils/DownloadHelper";

const BlueAlien = "#1976d2";
const WhiteAlien = "#ffffff";

export default function DownloadDialog({
  currentUserInfo,
  userRoleInfo,
  setShowMap,
  headerheight
}) {
  const controller = new AbortController();

  const { showGlobalLoader, setShowGlobalLoader } = useIFPRSGlobalContext();
  const navigate = useNavigate();
  const config = useSelector((state) => state.configuration.config);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isFailure, setIsFailure] = useState(true);
  const currentDate = new Date();
  const agencyList = [
    FieldNames.agencies.blm,
    FieldNames.agencies.bia,
    FieldNames.agencies.fws,
    FieldNames.agencies.nps,
    FieldNames.agencies.bor
    // No OWF
  ];

  const useStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
    condensedFormControl: {
      margin: "5px 15px 5px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControl: {
      margin: "10px 15px 10px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControlHeader: {
      margin: "0px 15px 0px 15px",
      minWidth: 120,
      width: "90%",
    },
    snackBarStyle: {
      top: "70px !important",
    },
  });
  const classes = useStyles();
  const theme = createTheme({
    overrides: {
      palette: {
        red: {
          main: "#FF0000",
          light: "#FF0000",
          dark: "#FF0000",
          contrastText: "#242105",
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: "green",
        },
      },
      MuiOutlinedInput: {
        root: {
          "& $notchedOutline": {
            //initial border
            borderColor: BlueAlien,
          },
          "&:hover $notchedOutline": {
            //border on hover
            borderColor: BlueAlien,
            border: "2px solid",
          },
          "&$focused $notchedOutline": {
            //border on focus
            borderColor: BlueAlien,
            border: "2px solid",
          },
        },
      },
    },
  });

  const [presetExpanded, setPresetExpanded] = useState(true);
  const handlePresetChange = () => {
    setPresetExpanded(!presetExpanded);
  };
  const [customExpanded, setCustomExpanded] = useState(true);
  const handleCustomChange = () => {
    setCustomExpanded(!customExpanded);
  };
  const [fuelsAllExpanded, setFuelsAllExpanded] = useState(false);
  const handleFuelsAllChange = (fuelsAllExpanded) => {
    setFuelsAllExpanded(!fuelsAllExpanded);
  };
  const [postFireAllExpanded, setPostFireAllExpanded] = useState(false);
  const handlePostFireAllChange = (postFireAllExpanded) => {
    setPostFireAllExpanded(!postFireAllExpanded);
  };
  const [nfporsExpanded, setNfporsExpanded] = useState(false);
  const [agencyExpanded, setAgencyExpanded] = useState({
    BLM: false,
    BIA: false,
    FWS: false,
    NPS: false
    // No OWF
  }); 
  const handleAgencyChange = (agency) => {
    setAgencyExpanded((prevState) => {
      let newState = _.cloneDeep(prevState);
      newState[agency] = !prevState[agency];
      return newState;
    });
  };

  const allVariants = [
    "doiAll",
    "doiCompletedActuals",
    "allCurrentFYBIL",
    "actualCurrentFYBIL",
    "estimateCurrentNextFYsBIL",
    "allCurrentFYRegular",
    "actualCurrentFYRegular",
    "estimateCurrentNextFYsRegular",
    "nfporsFuels",
    "nfporsPostFire",
    "nfporsGdb"
  ];

  const allDoiVariants = [
    "doiAll",
    "doiCompletedActuals",
    "allCurrentFYBIL",
    "actualCurrentFYBIL",
    "estimateCurrentNextFYsBIL",
    "allCurrentFYRegular",
    "actualCurrentFYRegular",
    "estimateCurrentNextFYsRegular",
  ];

  const allVariantNames = {
    doiAll: "All Treatments/Activities - All Years",
    doiCompletedActuals: "Completed Actual Treatments/Activities - All Years",
    allCurrentFYBIL: "All BIL Treatments/Activities - Current Fiscal Year",
    actualCurrentFYBIL: "Actual BIL Treatment/Activities - Current Fiscal Year",
    estimateCurrentNextFYsBIL: "Upcoming BIL Estimated Treatment/Activities",
    allCurrentFYRegular: "All Regular Treatments/Activities - Current Fiscal Year",
    actualCurrentFYRegular: "Actual Regular Treatment/Activities - Current Fiscal Year",
    estimateCurrentNextFYsRegular: "Upcoming Regular Estimated Treatment/Activities",
    nfporsFuelsBIA: "NFPORS - Fuels Archive (BIA)",
    nfporsFuelsBLM: "NFPORS - Fuels Archive (BLM)",
    nfporsFuelsBOR: "NFPORS - Fuels Archive (BOR)",
    nfporsFuelsFSSPF: "NFPORS - Fuels Archive (Forest Service S&PF)",
    nfporsFuelsFS: "NFPORS - Fuels Archive (Forest Service)",
    nfporsFuelsFWS: "NFPORS - Fuels Archive (FWS)",
    nfporsFuelsNPS: "NFPORS - Fuels Archive (NPS)",
    nfporsPostFire: "NFPORS - Post Fire Archive",
    nfporsGdb: "NFPORS - File Geodatabase"
  }

  const allDoiVariantNames = {
    doiAll: "All Treatments/Activities - All Years",
    doiCompletedActuals: "Completed Actual Treatments/Activities - All Years",
    allCurrentFYBIL: "All BIL Treatments/Activities - Current Fiscal Year",
    actualCurrentFYBIL: "Actual BIL Treatment/Activities - Current Fiscal Year",
    estimateCurrentNextFYsBIL: "Upcoming BIL Estimated Treatment/Activities",
    allCurrentFYRegular: "All Regular Treatments/Activities - Current Fiscal Year",
    actualCurrentFYRegular: "Actual Regular Treatment/Activities - Current Fiscal Year",
    estimateCurrentNextFYsRegular: "Upcoming Regular Estimated Treatment/Activities",
  }

  const allVariantDescriptions = {
    doiAll: "Download all non-archived Treatment/Activity details recorded within IFPRS across all fiscal years",
    doiCompletedActuals: "Download all non-archived Actual Treatments/Activities that are completed across all fiscal years",
    allCurrentFYBIL: "Download all non-archived Estimated and Actual Treatment/Activities for the current fiscal year with BIL funding",
    actualCurrentFYBIL: "Download all non-archived Actual Treatments/Activities for the current fiscal year with BIL funding",
    estimateCurrentNextFYsBIL: "Download all non-archived Estimated Treatments/Activities for the current and three upcoming fiscal years with BIL funding",
    allCurrentFYRegular: "Download all non-archived Estimated and Actual Treatment/Activities for the current fiscal year with Regular funding",
    actualCurrentFYRegular: "Download all non-archived Actual Treatments/Activities for the current fiscal year with Regular funding",
    estimateCurrentNextFYsRegular: "Download all non-archived Estimated Treatments/Activities for the current and three upcoming fiscal years with Regular funding",
    nfporsFuelsBIA: `Download all archived BIA Fuels Treatment/Activity details recorded within NFPORS from FY2003 into FY2025. Data was retrieved on ${config.nfporsDownloadDate}.`,
    nfporsFuelsBLM: `Download all archived BLM Fuels Treatment/Activity details recorded within NFPORS from FY2003 into FY2025. Data was retrieved on ${config.nfporsDownloadDate}.`,
    nfporsFuelsBOR: `Download all archived BOR Fuels Treatment/Activity details recorded within NFPORS from FY2003 into FY2025. Data was retrieved on ${config.nfporsDownloadDate}.`,
    nfporsFuelsFSSPF: `Download all archived Forest Service S&PF Fuels Treatment/Activity details recorded within NFPORS from FY2003 into FY2025. Data was retrieved on ${config.nfporsDownloadDate}.`,
    nfporsFuelsFS: `Download all archived Forest Service Fuels Treatment/Activity details recorded within NFPORS from FY2003 into FY2025. Data was retrieved on ${config.nfporsDownloadDate}.`,
    nfporsFuelsFWS: `Download all archived FWS Fuels Treatment/Activity details recorded within NFPORS from FY2003 into FY2025. Data was retrieved on ${config.nfporsDownloadDate}.`,
    nfporsFuelsNPS: `Download all archived NPS Fuels Treatment/Activity details recorded within NFPORS from FY2003 into FY2025. Data was retrieved on ${config.nfporsDownloadDate}.`,
    nfporsPostFire: `Download all archived Post Fire Treatment/Activity details recorded within NFPORS from FY2003 into FY2025. Data was retrieved on ${config.nfporsDownloadDate}.`,
    nfporsGdb: "Download a file geodatabase containing all archived Fuels/Post Fire data."
  }

  const allDoiVariantDescriptions = {
    doiAll: "Download all non-archived Treatment/Activity details recorded within IFPRS across all fiscal years",
    doiCompletedActuals: "Download all non-archived Actual Treatments/Activities that are completed across all fiscal years",
    allCurrentFYBIL: "Download all non-archived Estimated and Actual Treatment/Activities for the current fiscal year with BIL funding",
    actualCurrentFYBIL: "Download all non-archived Actual Treatments/Activities for the current fiscal year with BIL funding",
    estimateCurrentNextFYsBIL: "Download all non-archived Estimated Treatments/Activities for the current and three upcoming fiscal years with BIL funding",
    allCurrentFYRegular: "Download all non-archived Estimated and Actual Treatment/Activities for the current fiscal year with Regular funding",
    actualCurrentFYRegular: "Download all non-archived Actual Treatments/Activities for the current fiscal year with Regular funding",
    estimateCurrentNextFYsRegular: "Download all non-archived Estimated Treatments/Activities for the current and three upcoming fiscal years with Regular funding",
  }

  const agencyVariants = [
    "completedActualAllYears",
    "allCurrentFY",
    "actualsCurrentFY",
    "estimateCurrentNextFYs",
    "allCarryover",
  ];
  
  const agencyVariantNames = {
    completedActualAllYears: "Completed Actual Treatment/Activities - All Fiscal Years",
    allCurrentFY: "All Treatments/Activities - Current Fiscal Year",
    actualsCurrentFY: "Actual Treatment/Activities - Current Fiscal Year",
    estimateCurrentNextFYs: "Upcoming Estimated Treatment/Activities",
    allCarryover: "All Carryover Treatments/Activities",
  }

  const agencyVariantDescriptions = {
    completedActualAllYears: "Download all non-archived {0} Actual Treatments/Activities that are completed, across all fiscal years",
    allCurrentFY: "Download all non-archived {0} Estimated and Actual Treatment/Activities for the current fiscal year",
    actualsCurrentFY: "Download all non-archived {0} Actual Treatments/Activities for the current fiscal year",
    estimateCurrentNextFYs: "Download all non-archived {0} Estimated Treatments/Activities for the current and three upcoming fiscal years",
    allCarryover: "Download all non-archived {0} Treatments/Activities that are marked as carryover",
  }

  const agencyFileAddition = {
    FUELS: "_DOI",
    POSTFIRE: "_POSTFIRE",
    BLM: "_BLM",
    BIA: "_BIA",
    FWS: "_FWS",
    NPS: "_NPS",
    BOR: "_BOR",
  }

  const variantFileAddition = {
    doiAll: "ALL_FM",
    doiCompletedActuals: "Act_FM_Comp",
    completedActualAllYears: "_Act_FM_Comp",
    allCurrentFY: "_FM_CFY",
    actualsCurrentFY: "_Act_CFY",
    estimateCurrentNextFYs: "_FM_CFYplus3",
    allCarryover: "_Carryover",
    allCurrentFYBIL: "_BIL_FM_CFY",
    actualCurrentFYBIL: "_BIL_Act_CFY",
    estimateCurrentNextFYsBIL: "_BIL_FM_CFYplus3",
    allCurrentFYRegular: "_Reg_FM_CFY",
    actualCurrentFYRegular: "_Reg_Act_CFY",
    estimateCurrentNextFYsRegular: "_Reg_FM_CFYplus3",
  }

  const nfporsFileAndVariantName = {
    fuels: [
      {
        fileName: "Fuels_BIA",
        variantName: "nfporsFuelsBIA"
      },
      {
        fileName: "Fuels_BLM",
        variantName: "nfporsFuelsBLM"
      },
      {
        fileName: "Fuels_BOR",
        variantName: "nfporsFuelsBOR"
      },
      {
        fileName: "Fuels_Forest Service S&PF",
        variantName: "nfporsFuelsFSSPF"
      },
      {
        fileName: "Fuels_Forest Service",
        variantName: "nfporsFuelsFS"
      },
      {
        fileName: "Fuels_FWS",
        variantName: "nfporsFuelsFWS"
      },
      {
        fileName: "Fuels_NPS",
        variantName: "nfporsFuelsNPS"
      }
    ],
    postFire: [
      {
        fileName: "PostFire",
        variantName: "nfporsPostFire"
      }
    ]
  }

  let [downloadsHeight, setDownloadsHeight] = useState(`calc(100vh - ${headerheight} - 64px)`);

  useEffect(() => {
    const downloadsHeaderHeight = document.querySelector('#downloads-title').getBoundingClientRect().height;
    setDownloadsHeight(`calc(100vh - ${headerheight}px - ${downloadsHeaderHeight}px)`);
  });

  useEffect(() => {     
     return () => {
      controller.abort();
     }
  }, [])

  useEffect(() => {
    setShowMap(false);
  }, [setShowMap]);

  const handleClose = () => {
    setShowMap(true);
    navigate("/home");
  };

  const downloadFileThreeDayWindow = (nameStr, descriptionStr, fileExtension) => {
    let fileNameCallback = (retries) => {
      return `IFPRS${nameStr}${descriptionStr}_${formatDateForDownloadFiles(getDaysBeforeDate(retries + 1))}`;
    }
    requestSingleDownload(
        fileNameCallback,
        fileExtension,
        controller,
        config,
        setShowGlobalLoader,
        3
    );
  }

  const getDownloadLinks = (agency, variant) => {
    let nameStr = agencyFileAddition[agency] ? agencyFileAddition[agency] : "";
    if(agency === "FUELS" && variant !== "doiAll" && variant !== "doiCompletedActuals")
    {
      // The BIL/Regular downloads are named slightly differently
      nameStr = "";
    }
    let descriptionStr = variantFileAddition[variant] ? variantFileAddition[variant].replace("{0}", agency) : "";

    return(
      <>
        <Link
          component="button"
          variant="body2"
          disabled={showGlobalLoader}
          onClick={() => {
            setShowGlobalLoader(true);
            downloadFileThreeDayWindow(nameStr, descriptionStr, ".csv");
          }}
          style={{ marginRight: "5px", fontSize: "18px" }}
        >
          csv
        </Link>
        <Link
          component="button"
          variant="body2"
          disabled={showGlobalLoader}
          onClick={() => {
            setShowGlobalLoader(true);
            downloadFileThreeDayWindow(nameStr, descriptionStr, ".xlsx");
          }}
          style={{ marginRight: "5px", fontSize: "18px" }}
        >
          excel
        </Link>
      </>
    )
  }

  const getDownloadCard = (isAgency, agency, variant) => {
    const nameList = isAgency ? agencyVariantNames : allVariantNames;
    const descriptionList = isAgency ? agencyVariantDescriptions : allVariantDescriptions;
    let nameStr = nameList[variant] ? nameList[variant] : "";
    let descriptionStr = descriptionList[variant] ? descriptionList[variant].replace("{0}", agency) : "";

    return(
      <Box key={agency + "-" + variant}>
        <Paper sx={{ marginBottom: '5px', marginLeft: '5px' }}>
          <div style={{ marginLeft: '5px', marginRight: '5px' }}>
            <Typography
              variant="h5"
              sx={{ textDecoration: "underline" }}
            >
              {nameStr}
            </Typography>
            <Box>
              <Typography
                display={"inline"}
                sx={{ fontWeight: "bold" }}
              >
                Description:
              </Typography>
              <Typography
                  display={"inline"}
                  sx={{ marginLeft: '5px' }}
              >
                {descriptionStr}
              </Typography>
            </Box>
            {getDownloadLinks(agency, variant)}
          </div>
        </Paper>
      </Box>
    )
  }

  // Another function that calls getNFPORSDownloadCard for every agency file addition
  const getNFPORSDownloadCardsAllAgencies = (isFuels) => {
    if (isFuels) {
      return (
        <Accordion
          style={{
            backgroundColor: ColorConstants.LightGrayAlien,
            marginLeft: '5px',
            marginBottom: '5px'
          }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`inner-panel-content-nfpors-fuels`}
              id={`inner-panel-header-nfpors-fuels`}
            >
              <Typography variant="h5">Fuels</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {
                nfporsFileAndVariantName[isFuels ? "fuels" : "postFire"].map(item => {
                  return getSimpleLinkCard(
                    'csv',
                    `${generateStaticDownloadUrl(`NFPORS/${item.fileName}`, '.csv', config )}`,
                    item.variantName
                  )
                })
              }
            </AccordionDetails>
          </Accordion>
      )
    } else {
      return (
        // No accordion for post fire since there's only one file
        nfporsFileAndVariantName[isFuels ? "fuels" : "postFire"].map(item => {
          return getSimpleLinkCard(
            'csv',
            `${generateStaticDownloadUrl(`NFPORS/${item.fileName}`, '.csv', config )}`,
            item.variantName
          )
        })
      )
    }
  }


  const getSimpleLinkCard = (linkText, linkUrl, variant) => {
    return (
      <Box key={ `${variant}-${linkText}` }>
        <Paper sx={{marginBottom: '5px', marginLeft: '5px'}}>
          <div style={{ marginLeft: '5px', marginRight: '5px' }}>
            <Typography
              variant="h5"
              sx={{ textDecoration: "underline" }}
            >
              {allVariantNames[variant]}
            </Typography>
            <Box>
              <Typography
                display={"inline"}
                sx={{ fontWeight: "bold" }}
              >
                Description:
              </Typography>
              <Typography display={"inline"} sx={{ marginLeft: '5px' }}>
                {allVariantDescriptions[variant]}
              </Typography>
            </Box>
              <Link
                variant="body2"
                disabled={showGlobalLoader}
                href={ linkUrl }
                target="_blank"
                rel="noopener"
                style={{ marginRight: "5px", fontSize: "18px" }}
              >
                { linkText }
              </Link>
          </div>
        </Paper>
      </Box>
    )
  }

  return (
    <Box style={{ backgroundColor: ColorConstants.MidGray }}>
      <AppBar
        id="downloads-title"
        sx={{ position: "relative" }}
        style={{ backgroundColor: BlueAlien, marginTop: headerheight }}
      >
        <Toolbar>
          <FormControl variant="standard" className={classes.formControl}>
            <ThemeProvider theme={theme}>
              <Grid
                item
                size={12}
                container
                direction="row"
                className="grid-item"
                style={{ alignItems: "center" }}
              >
                <Grid item size={0.2}>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="close"
                  >
                    <CloseIcon style={{ fill: WhiteAlien }} />
                  </IconButton>
                </Grid>
                <Grid item size={4}>
                  <Typography
                    sx={{ ml: 2, flex: 1 }}
                    variant="h6"
                    component="div"
                    style={{ color: WhiteAlien }}
                  >
                    Downloads
                  </Typography>
                </Grid>
              </Grid>
            </ThemeProvider>
          </FormControl>
        </Toolbar>
      </AppBar>
      {/*  Site Header (red) = 150px; Downloads banner (blue) = 64px */}
      {/*todo: replace 150px with headerheight defaulted to 150px in app.js but could be modified somewhere along the line*/}
      <Paper
        style={{
          height: downloadsHeight,
          overflow: "auto",
          backgroundColor: ColorConstants.MidGray,
        }}
      >
        <Box
          sx={{
            width: "100%",
            minWidth: "10px",
            backgroundColor: ColorConstants.MidGray,
          }}
        >
          <Accordion
            expanded={presetExpanded}
            style={{ backgroundColor: ColorConstants.LightGrayAlien }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
              onClick={() => {
                handlePresetChange();
              }}
            >
              <Typography variant="h3">Downloads</Typography>
            </AccordionSummary>
            {/* The below is being left in as they haven't decided if they would like live downloads back in the future */}
            {/* <AccordionDetails>
                <Paper>
                  <Typography variant="h5" sx={{textDecoration: 'underline'}}>DOI - All - Live</Typography>
                  <Box>
                  <Typography display={"inline"} sx={{fontWeight: 'bold'}}>Description:</Typography><Typography display={"inline"}> Download all non-archived Treatment/Activity details recorded within IFPRS</Typography>
                  <Typography sx={{color: 'red'}}>Warning: This may take up to several minutes to compile and may comprise a large download file</Typography>
                  </Box>
                  <Link
                    component="button"
                    variant="body2"
                    disabled={showGlobalLoader}
                    onClick={() => {
                      setShowGlobalLoader(true);
                      downloadPresetCsvLive(downloadPresetVariants.all);
                    }}
                    sx={{marginRight: '5px', fontSize: '18px'}}
                  >
                    csv
                  </Link>
                  <Link
                    component="button"
                    variant="body2"
                    disabled={showGlobalLoader}
                    onClick={() => {
                      setShowGlobalLoader(true);
                      downloadPresetExcelLive(downloadPresetVariants.all);
                    }}
                    sx={{marginRight: '5px', fontSize: '18px'}}
                  >
                    excel
                  </Link>
                </Paper>
              </AccordionDetails> */}
            <AccordionDetails>
              <Grid
                item
                size={12}
                container
                direction="row"
                className="grid-item"
                style={{ alignItems: "center" }}
              >
                <Grid item size={4}>
                  <Box
                    sx={{ backgroundColor: "#D3D3D3", marginBottom: "10px" }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <HelpIcon
                      fontSize="small"
                      color="primary"
                      style={{ paddingRight: "5px" }}
                    />
                    <Typography fontSize={12}>
                      The download files on this page are generated at midnight
                      Alaska time each day unless otherwise noted.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Accordion
                expanded={fuelsAllExpanded}
                style={{ backgroundColor: ColorConstants.MidLightGrayAlien }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  onClick={() => {
                    handleFuelsAllChange(fuelsAllExpanded);
                  }}
                >
                  <Typography variant="h4">DOI - All</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {allDoiVariants.map((variant) => {
                    return getDownloadCard(false, "FUELS", variant);
                  })}
                </AccordionDetails>
              </Accordion>
              {agencyList.map((agency) => {
                return (
                  <Accordion
                    key={agency}
                    expanded={
                      agencyExpanded[agency] ? agencyExpanded[agency] : false
                    }
                    style={{
                      backgroundColor: ColorConstants.MidLightGrayAlien,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                      onClick={() => {
                        handleAgencyChange(agency);
                      }}
                    >
                      <Typography variant="h4">{agency}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {agencyVariants.map((variant) => {
                        return getDownloadCard(true, agency, variant);
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
              <Accordion
                expanded={postFireAllExpanded}
                style={{ backgroundColor: ColorConstants.MidLightGrayAlien }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  onClick={() => {
                    handlePostFireAllChange(postFireAllExpanded);
                  }}
                >
                  <Typography variant="h4">Post Fire - All</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {
                    getDownloadCard(false, "POSTFIRE", "doiAll")
                  }
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={nfporsExpanded}
                style={{
                  backgroundColor: ColorConstants.MidLightGrayAlien
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  onClick={() => {
                    setNfporsExpanded(!nfporsExpanded);
                  }}
                >
                  <Typography variant="h4">NFPORS - Legacy</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  { getNFPORSDownloadCardsAllAgencies(true) }
                  { getNFPORSDownloadCardsAllAgencies(false) }
                  {
                    getSimpleLinkCard(
                      'File Geodatabase',
                      config.nfporsFileGDBDownload.url,
                      "nfporsGdb"
                    )
                  }
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box>
          <ThemeProvider theme={theme}>
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={6000}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              className={classes.snackBarStyle}
              onClose={() => {
                setSnackbarOpen(false);
                setSnackbarMessage("");
              }}
              sx={{ zIndex: 9999 }}
            >
              <Alert
                onClose={() => {
                  setSnackbarOpen(false);
                  setSnackbarMessage("");
                }}
                severity={isFailure ? "error" : "success"}
                sx={{ width: "100%" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </ThemeProvider>
        </Box>
      </Paper>
    </Box>
  );
}
